'use client';

import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useSession } from 'next-auth/react';

import {
  ATTRIBUTE_FLOWER_TYPE_PATH,
  ATTRIBUTE_PRICE_RANGE_PATH
} from '@page-layouts/catalog/FiltersBar/options/constants';
import { SEFUrlsService } from '@services';
import { ICatalogDataContext, ICatalogDataHandler, ICatalogResponse, IFetchCatalogData } from '@types';
import {
  getCatalogData,
  getCategoryCatalogData,
  getFavoritesCatalogData,
  getRecentlyViewedCatalogData,
  getSpecialOffersCatalogData
} from '../api/catalog';

const CatalogDataContext = createContext<ICatalogDataContext | undefined>(undefined);

const CatalogDataProvider = ({
  initialData,
  categoryId,
  children
}: {
  initialData?: ICatalogResponse;
  children: ReactNode;
  categoryId?: string;
}) => {
  const searchParams = useSearchParams();
  const path = usePathname();
  const searchParamsString = searchParams.toString();
  const [currentPage, setCurrentPage] = useState(1);
  const [catalog, setCatalog] = useState<ICatalogResponse | null>(initialData || null);
  const [isLoading, setIsLoading] = useState(false);
  const session = useSession();
  const token = session?.data?.user.bearer ?? '';

  const fetchCatalogData: IFetchCatalogData = async ({ query, categoryId, loadMore }) => {
    if (isLoading) return;

    let handler: ICatalogDataHandler | null = null;
    if (path === '/sales') {
      handler = getSpecialOffersCatalogData;
    } else if (path.startsWith('/tp/profile/favorites')) {
      handler = getFavoritesCatalogData;
    } else if (path.startsWith('/tp/profile/recently-viewed')) {
      handler = getRecentlyViewedCatalogData;
    } else if (categoryId) {
      handler = getCategoryCatalogData;
    } else {
      handler = getCatalogData;
    }

    const queryStr = query
      .replace(ATTRIBUTE_PRICE_RANGE_PATH, 'priceRange')
      .replace(ATTRIBUTE_FLOWER_TYPE_PATH, 'flowerType');

    setIsLoading(true);
    return handler({ query: queryStr, categoryId, token })
      .then((data) => {
        if (loadMore) {
          setCatalog((state) => ({
            ...data,
            docs: [...(state ? state.docs : []), ...data.docs]
          }));
        } else {
          setCatalog(data);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch more products data:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    // * fetch initial catalog data except for the category page
    // * because the category page already fetches the data on route resolver step (for SEO purposes)
    if (!categoryId || !initialData) {
      fetchCatalogData({ query: searchParamsString });
    }
  }, []);

  useEffect(() => {
    if (isLoading) return;

    if (categoryId && initialData && JSON.stringify(initialData.docs) !== JSON.stringify(catalog?.docs)) {
      setCatalog(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    if (searchParams.get('pageNumber') && categoryId) {
      const pageNumber = Number(searchParams.get('pageNumber'));
      if (pageNumber !== currentPage) {
        setTimeout(() => {
          window.scrollTo({ top: 0 });
        }, 10);
        setCurrentPage(pageNumber);
        fetchCatalogData({ query: searchParamsString, categoryId });
      }
    }
  }, [searchParams]);

  const loadMoreProducts = (page: number) => {
    const params = new URLSearchParams(searchParamsString);
    params.set('pageNumber', String(page));

    let queryStr = '';
    if (SEFUrlsService.extractedFilterValues?.length) {
      queryStr = SEFUrlsService.queryString;
    } else {
      SEFUrlsService.queryString = '';
    }

    setCurrentPage(page);
    fetchCatalogData({ query: (queryStr ? `${queryStr}&` : '') + params.toString(), categoryId, loadMore: true });
  };

  return (
    <CatalogDataContext.Provider value={{ catalog, setCatalog, isLoading, loadMoreProducts }}>
      {children}
    </CatalogDataContext.Provider>
  );
};

const useCatalogData = () => {
  const context = useContext(CatalogDataContext);
  if (!context) {
    throw new Error('useCatalogData must be used within a CatalogDataProvider');
  }
  return context;
};

export { CatalogDataProvider, useCatalogData };
